import { zodResolver } from '@hookform/resolvers/zod';
import {
  useController,
  useForm,
  type Control,
  type DefaultValues,
  type FieldPathByValue,
  type FieldValues,
} from 'react-hook-form';
import { z } from 'zod';
import { useT } from '@/i18n';

function issueText(issue: z.core.$ZodRawIssue, text: ReturnType<typeof useT>['forms']): string {
  if (issue.code === 'custom' && issue.message) return issue.message;
  if (issue.code === 'too_small' && Number(issue.minimum) === 1) return text.required;
  if (issue.code === 'too_big' && issue.origin === 'string') {
    return text.tooLong(Number(issue.maximum));
  }
  if (issue.code === 'invalid_format' && issue.format === 'email') return text.invalidEmail;
  if (issue.code === 'invalid_format' && issue.format === 'url') {
    return issue.input === '' ? text.required : text.invalidUrl;
  }
  return text.invalid;
}

export function useContractForm<TValues extends FieldValues, TBody>(
  schema: z.ZodType<TBody>,
  options: {
    defaultValues: DefaultValues<TValues>;
    values?: TValues;
    toBody: (values: TValues) => unknown;
  },
) {
  const text = useT().forms;
  return useForm<TValues, unknown, TBody>({
    resolver: zodResolver(z.preprocess(options.toBody, schema), {
      error: (issue) => issueText(issue, text),
    }),
    defaultValues: options.defaultValues,
    values: options.values,
    resetOptions: { keepDirtyValues: true },
    mode: 'onChange',
  });
}

export function useListInput<TValues extends FieldValues>(
  control: Control<TValues, unknown, unknown>,
  name: FieldPathByValue<TValues, string>,
) {
  const { field, fieldState } = useController({ control, name });
  const error = fieldState.error?.message;
  return {
    value: field.value,
    onInput: (event: { target: HTMLInputElement }) => field.onChange(event.target.value),
    onBlur: field.onBlur,
    errorMessage: error,
    errorMessageForce: error !== undefined,
  };
}
